import { useEffect, useState } from 'react';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';
import { installedPlugins, runPlugin, savePlugin, removePlugin, type PluginDef } from '../plugins.ts';
import { insertWidget, SAMPLE_WIDGETS } from '../widgets.ts';

const isWidget = (code: string) => /widget\.register\s*\(/.test(code);

const TEMPLATE = `// The vellum API (all async): getSelection(), getNodes(ids, {deep}), find({name, type, text}),
// tree(), createNodes(specs, parentId?), updateNodes([{id, ...spec}]), deleteNodes(ids), select(ids),
// notify(text), showUI(html, {width, height, title}), onMessage(fn), postMessage(msg), close().
// Specs are the same JSON agents use, e.g. {type: 'sticky', text: 'Hi'}.
// Figma plugins (code using figma.*) run as they are: paste code.js here and ui.html below.
const sel = await vellum.getSelection();
vellum.notify('You have ' + sel.length + ' layer(s) selected');
vellum.close();`;

export function PluginsDialog() {
  const open = useApp('ui', () => app.ui.pluginsOpen);
  const [list, setList] = useState<PluginDef[]>(() => installedPlugins());
  const [draft, setDraft] = useState<PluginDef | null>(null);
  const [tab, setTab] = useState<'installed' | 'community'>('installed');
  if (!open) return null;
  const close = () => (app.setUI({ pluginsOpen: false }), setDraft(null));
  const publish = async (p: PluginDef) => {
    const description = prompt(`Describe “${p.name}” for others`, '') ?? '';
    const r = await fetch('/api/community', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ id: p.communityId, name: p.name, description, code: p.code, ui: p.ui }) });
    const body = (await r.json()) as { item?: { id: string }; error?: string };
    if (!r.ok || !body.item) return app.toast(body.error ?? 'Could not publish');
    savePlugin({ ...p, communityId: body.item.id });
    setList(installedPlugins());
    app.toast(`Published “${p.name}” to the community`);
  };
  return (
    <div className="modal-backdrop" onPointerDown={(e) => e.target === e.currentTarget && close()}>
      <div className="modal" style={{ width: 'min(620px, calc(100vw - 32px))' }}>
        <header>
          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span>Plugins</span>
            <button className={`btn ${tab === 'installed' ? 'primary' : 'ghost'}`} onClick={() => (setTab('installed'), setDraft(null))}>
              Installed
            </button>
            <button className={`btn ${tab === 'community' ? 'primary' : 'ghost'}`} onClick={() => (setTab('community'), setDraft(null))}>
              Community
            </button>
          </span>
          <button className="icon-btn" onClick={close}>
            ✕
          </button>
        </header>
        <div className="body">
          {tab === 'community' && !draft ? (
            <CommunityList onInstalled={() => setList(installedPlugins())} onClose={close} />
          ) : draft ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                savePlugin(draft);
                setList(installedPlugins());
                setDraft(null);
              }}
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              <div className="field">
                <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Plugin name" required style={{ border: 0, background: 'transparent', outline: 'none', width: '100%' }} onKeyDown={(e) => e.stopPropagation()} />
              </div>
              <textarea className="plugin-code" value={draft.code} spellCheck={false} onChange={(e) => setDraft({ ...draft, code: e.target.value })} onKeyDown={(e) => e.stopPropagation()} />
              <details>
                <summary style={{ cursor: 'pointer', color: 'var(--text-2)' }}>Figma plugin? Paste its ui.html here (available as __html__)</summary>
                <textarea className="plugin-code" style={{ height: 140, width: '100%' }} value={draft.ui ?? ''} spellCheck={false} onChange={(e) => setDraft({ ...draft, ui: e.target.value || undefined })} onKeyDown={(e) => e.stopPropagation()} />
              </details>
              <details open={!!draft.parameters?.length}>
                <summary style={{ cursor: 'pointer', color: 'var(--text-2)' }}>Quick-action parameters (the manifest's "parameters" array)</summary>
                <textarea
                  className="plugin-code"
                  style={{ height: 90, width: '100%' }}
                  defaultValue={draft.parameters ? JSON.stringify(draft.parameters, null, 1) : ''}
                  placeholder='[{"key": "color", "name": "Color", "allowFreeform": true}]'
                  spellCheck={false}
                  onBlur={(e) => {
                    const raw = e.target.value.trim();
                    if (!raw) return setDraft({ ...draft, parameters: undefined });
                    try {
                      const v = JSON.parse(raw) as unknown;
                      const list = (Array.isArray(v) ? v : (v as { parameters?: unknown[] }).parameters) as PluginDef['parameters'];
                      if (!Array.isArray(list) || list.some((p) => !p || !p.key || !p.name)) throw new Error('each parameter needs a key and a name');
                      setDraft({ ...draft, parameters: list });
                    } catch (err) {
                      app.toast(`Parameters: ${(err as Error).message}`);
                    }
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </details>
              <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                <span style={{ flex: 1, color: 'var(--text-2)' }}>Runs sandboxed: it can only reach this file through the API, and every edit is undoable.</span>
                <button type="button" className="btn secondary" onClick={() => (isWidget(draft.code) ? insertWidget(draft.name, draft.code) : runPlugin(draft))}>
                  {isWidget(draft.code) ? 'Insert widget' : 'Run'}
                </button>
                <button type="button" className="btn secondary" onClick={() => setDraft(null)}>
                  Cancel
                </button>
                <button className="btn primary" type="submit">
                  Save
                </button>
              </div>
            </form>
          ) : (
            <>
              {list.map((p) => (
                <div key={p.id} className="plugin-row">
                  <span style={{ flex: 1 }}>
                    {p.name}
                    {p.builtIn && <span style={{ color: 'var(--text-2)' }}> · built in</span>}
                  </span>
                  <button className="btn primary" onClick={() => (isWidget(p.code) ? insertWidget(p.name, p.code) : runPlugin(p), close())}>
                    {isWidget(p.code) ? 'Insert widget' : 'Run'}
                  </button>
                  {!p.builtIn && (
                    <>
                      <button className="btn secondary" onClick={() => setDraft(p)}>
                        Edit
                      </button>
                      <button className="btn secondary" onClick={() => (removePlugin(p.id), setList(installedPlugins()))}>
                        Remove
                      </button>
                      <button className="btn ghost" title="Share it with everyone on this server" onClick={() => publish(p)}>
                        {p.communityId ? 'Update' : 'Publish'}
                      </button>
                      {p.communityId && (
                        <button className="btn ghost" title="What to commit to the public index repository to share it with everyone" onClick={() => void showSubmission(p.communityId!)}>
                          Submit publicly
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
              <h5>Widgets</h5>
              {SAMPLE_WIDGETS.map((w) => (
                <div key={w.name} className="plugin-row">
                  <span style={{ flex: 1 }}>
                    {w.name} <span style={{ color: 'var(--text-2)' }}>· widget, built in</span>
                  </span>
                  <button className="btn primary" onClick={() => (insertWidget(w.name, w.code), close())}>
                    Insert widget
                  </button>
                </div>
              ))}
              <button className="btn secondary" style={{ marginTop: 10 }} onClick={() => setDraft({ id: 'p' + Math.random().toString(36).slice(2, 9), name: 'My plugin', code: TEMPLATE })}>
                + New plugin
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface CommunityItem {
  id: string;
  name: string;
  description: string;
  kind: 'plugin' | 'widget';
  author: { name: string };
  installs: number;
  code?: string;
  ui?: string;
}

/** The public index submission for a plugin published on this server: files to commit, and the index entry. */
async function showSubmission(id: string) {
  const r = await fetch(`/api/community/${id}/submission`);
  if (!r.ok) return app.toast('Publish it on this server first');
  const sub = (await r.json()) as { entry: Record<string, unknown>; files: Record<string, string> };
  const text = `Add these files to the public index repository, and the entry to index.json "items":\n\n${Object.keys(sub.files).join('\n')}\n\n${JSON.stringify(sub.entry, null, 2)}\n`;
  try {
    await navigator.clipboard.writeText(text + Object.entries(sub.files).map(([f, c]) => `\n--- ${f}\n${c}`).join('\n'));
    app.toast('Copied the submission (files and index entry) to the clipboard');
  } catch {
    app.toast('Could not copy the submission');
  }
}

/** Plugins and widgets published on this server or in the public index: look at the code, then install or insert. */
function CommunityList({ onInstalled, onClose }: { onInstalled: () => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<'server' | 'public'>('server');
  const [items, setItems] = useState<CommunityItem[] | null>(null);
  const [publicState, setPublicState] = useState<{ configured: boolean; url?: string; error?: string }>({ configured: true });
  const [code, setCode] = useState<{ id: string; text: string } | null>(null);
  const base = scope === 'public' ? '/api/community/public' : '/api/community';
  useEffect(() => {
    setItems(null);
    const t = setTimeout(() => {
      fetch(`${base}?q=${encodeURIComponent(q)}`)
        .then(async (r) => ({ ok: r.ok, d: (await r.json()) as { items: CommunityItem[]; configured?: boolean; url?: string; error?: string } }))
        .then(({ ok, d }) => {
          if (scope === 'public') setPublicState({ configured: d.configured !== false, url: d.url, error: ok ? undefined : d.error });
          setItems(d.items ?? []);
        })
        .catch(() => setItems([]));
    }, 150);
    return () => clearTimeout(t);
  }, [q, scope, base]);
  const full = async (id: string) => ((await (await fetch(`${base}/${id}`)).json()) as { item: CommunityItem }).item;
  const install = async (it: CommunityItem) => {
    const r = await fetch(`${base}/${it.id}/install`, { method: 'POST' });
    const body = (await r.json()) as { item?: CommunityItem; error?: string };
    if (!r.ok || !body.item) return app.toast(body.error ?? 'Install failed');
    const item = body.item;
    if (item.kind === 'widget') {
      insertWidget(item.name, item.code!);
      onClose();
      return;
    }
    // installing again (or your own published plugin) updates the copy you have
    const existing = installedPlugins().find((p) => p.communityId === item.id);
    savePlugin({ id: existing?.id ?? `c-${item.id}`, name: item.name, code: item.code!, ui: item.ui, communityId: item.id });
    onInstalled();
    app.toast(existing ? `Updated “${item.name}”` : `Installed “${item.name}”`);
  };
  return (
    <>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <button className={`btn ${scope === 'server' ? 'secondary' : 'ghost'}`} onClick={() => setScope('server')}>
          This server
        </button>
        <button className={`btn ${scope === 'public' ? 'secondary' : 'ghost'}`} onClick={() => setScope('public')} title="The shared public index (VELLUM_COMMUNITY_INDEX)">
          Public index
        </button>
      </div>
      {scope === 'public' && !publicState.configured && <div style={{ color: 'var(--text-2)', marginBottom: 8 }}>No public index is set up on this server. Start it with VELLUM_COMMUNITY_INDEX=&lt;url of index.json&gt;.</div>}
      {scope === 'public' && publicState.error && <div style={{ color: 'var(--danger, #e5484d)', marginBottom: 8 }}>{publicState.error}</div>}
      <div className="field" style={{ marginBottom: 8 }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search plugins and widgets" style={{ border: 0, background: 'transparent', outline: 'none', width: '100%' }} onKeyDown={(e) => e.stopPropagation()} />
      </div>
      {items === null && <div style={{ color: 'var(--text-2)' }}>Loading…</div>}
      {items?.length === 0 && publicState.configured && <div style={{ color: 'var(--text-2)' }}>{scope === 'server' ? 'Nothing published yet. Publish one of your plugins from the Installed tab.' : 'Nothing matches in the public index.'}</div>}
      {items?.map((it) => (
        <div key={it.id}>
          <div className="plugin-row">
            <span style={{ flex: 1 }}>
              <b>{it.name}</b> <span style={{ color: 'var(--text-2)' }}>· {it.kind} by {it.author.name}{scope === 'server' ? ` · ${it.installs} install${it.installs === 1 ? '' : 's'}` : ' · verified against the index'}</span>
              {it.description && <div style={{ color: 'var(--text-2)' }}>{it.description}</div>}
            </span>
            <button className="btn ghost" onClick={async () => setCode(code?.id === it.id ? null : { id: it.id, text: (await full(it.id)).code ?? '' })}>
              {code?.id === it.id ? 'Hide code' : 'View code'}
            </button>
            <button className="btn primary" disabled={app.readOnly && it.kind === 'widget'} onClick={() => install(it)}>
              {it.kind === 'widget' ? 'Insert' : 'Install'}
            </button>
          </div>
          {code?.id === it.id && <pre className="dev-code" style={{ maxHeight: 220 }}>{code.text}</pre>}
        </div>
      ))}
      <div style={{ color: 'var(--text-2)', marginTop: 8 }}>Community plugins run in the same sandbox as your own: they can only reach the open file through the plugin API, and every edit can be undone.</div>
    </>
  );
}
